import { TFunction, useTranslation } from 'react-i18next';
import {
  APIActions,
  Box,
  Button,
  CustomDialog,
  HtmlTrans,
  IconName,
  MUIDataTable,
  TABLE_INITIAL_STATE,
  TableActionTypes,
  TableState,
  Typography,
  useDataTable,
} from 'web-library';
import React, { useEffect, useState } from 'react';

import { RouteComponentProps, useHistory } from 'react-router-dom';
import EditButton from 'web-library/components/buttons/EditButton';
import DeleteButton from 'web-library/components/buttons/DeleteButton';
import {
  generateAuthorityName,
  NOTIFICATION_FUNCTION_MAP,
} from 'web-library/models/AuthorityFunctions';
import {
  FunctionModule,
  PermissionType,
} from 'web-library/models/UserGroupAccessConfiguration/UserGroupAccessConfiguration';
import IconWithLabel from 'web-library/stories/atom/IconWithLabel/IconWithLabel';
import { stringToSemanticDisplay } from '../../../../utils/DateUtils';
import { AnnouncementNotificationTitleUuid } from '../../../manageAnnouncements/ManageAnnouncementsListing';
import { useDeleteNotificationTemplate } from '../../../../api-refactored/manage-notifications/useDeleteNotificationTemplate';
import {
  NotificationTemplateListingResponseContentObject,
  usePostNotificationTemplateListing,
} from '../../../../api-refactored/manage-notifications/usePostNotificationTemplateListing';
import { useStyles } from '../index';
import RouteKey from '../../../../routes/key';
import { useGetUserAuthorities } from '../../../../api-refactored/user/useGetUserAuthorities';

const { admin } = RouteKey.child;
const {
  manageNotifications,
  editEmailTemplate,
  editSMSTemplate,
  sendEmailNotification,
  sendSMSNotification,
} = RouteKey.admin.child;

const selectFeatureSubPathEditTemplate = (feature: string) => {
  if (feature === 'EMAIL') {
    return editEmailTemplate.route;
  }
  if (feature === 'SMS') {
    return editSMSTemplate.route;
  }
  return null;
};

const selectFeatureSubPathSendNotification = (feature: string) => {
  if (feature === 'EMAIL') {
    return sendEmailNotification.route;
  }
  if (feature === 'SMS') {
    return sendSMSNotification.route;
  }
  return null;
};

const displayDateTimeUpdated = (
  data: NotificationTemplateListingResponseContentObject[],
  dataIndex: number,
  classes: Record<string,string>
) => (
  <>
    {stringToSemanticDisplay(data[dataIndex].dateUpdated)}
    <br />
    <Typography
      className={classes.updatedByText}
      variant="subtitle2"
    >{`Updated by ${data[dataIndex].updatedBy}`}</Typography>
  </>
);

const tableColumns = (
  featureValue: string,
  state: TableState,
  classesHistoryTranslation: {
    classes: Record<string,string>;
    history: RouteComponentProps['history'];
    t: TFunction<'translation'>;
  },
  setDeleteNotificationTemplateTitleUuid: React.Dispatch<
    React.SetStateAction<AnnouncementNotificationTitleUuid>
  >,
  setOpenDeleteModal: React.Dispatch<React.SetStateAction<boolean>>,
  isFeatureValueSMS: boolean,
  authorities?: string[]
) => [
  {
    name: 'title',
    label: classesHistoryTranslation.t('announcementsNotifications->>Listing.Title'),
    options: {
      sort: false,
      setCellProps: () => ({ style: { paddingTop: '20px' } }),
      customBodyRenderLite: (dataIndex: number) => (
        <Typography className={classesHistoryTranslation.classes.titleDescription}>
          {state.data[dataIndex].title}
        </Typography>
      ),
    },
  },
  {
    name: 'message',
    label: classesHistoryTranslation.t('announcementsNotifications->>Listing.Message'),
    options: {
      sort: false,
      display: !isFeatureValueSMS,
      setCellProps: () => ({ style: { paddingTop: '20px' } }),
      customBodyRenderLite: (dataIndex: number) => (
        <Typography className={classesHistoryTranslation.classes.titleDescription}>
          {state.data[dataIndex].message || '-'}
        </Typography>
      ),
    },
  },
  {
    name: 'updatedAt',
    label: classesHistoryTranslation.t('announcementsNotifications->>Listing.DateUpdated'),
    options: {
      setCellProps: () => ({ style: { paddingTop: '20px' } }),
      customBodyRenderLite: (dataIndex: number) =>
        displayDateTimeUpdated(state.data, dataIndex, classesHistoryTranslation.classes),
    },
  },
  {
    name: 'actions',
    label: classesHistoryTranslation.t('announcementsNotifications->>Listing.Actions'),
    options: {
      sort: false,
      setCellProps: () => ({ style: { width: '25%', paddingTop: '20px' } }),
      customBodyRenderLite: (dataIndex: number) => (
        <>
          {authorities?.includes(
            generateAuthorityName(
              'CP',
              FunctionModule.ADMIN,
              NOTIFICATION_FUNCTION_MAP[featureValue],
              PermissionType.CREATE
            )
          ) && (
            <Box
              className={`${classesHistoryTranslation.classes.iconButtonContainer} ${classesHistoryTranslation.classes.mailButtonContainer}`}
            >
              <Button
                style={{ padding: 0, minWidth: 0 }}
                color="primary"
                variant="text"
                onClick={() =>
                  classesHistoryTranslation.history.push(
                    `/${admin}/${manageNotifications.route}/${
                      state.data[dataIndex].uuid
                    }/${selectFeatureSubPathSendNotification(featureValue)}`,
                    {
                      featureValue,
                      defaultTab: 1,
                      page: state.page,
                      pageSize: state.size,
                    }
                  )
                }
                startIcon={
                  <IconWithLabel
                    iconSize="M"
                    iconName={IconName.mail}
                    label={classesHistoryTranslation.t(
                      'announcementsNotifications->>Listing.UseTemplate'
                    )}
                    typographyProps={{
                      variant: 'h6',
                      align: 'left',
                    }}
                    className={classesHistoryTranslation.classes.useTemplateIconContainer}
                    typographyClassName={classesHistoryTranslation.classes.useTemplateLabel}
                  />
                }
                size="small"
              />
            </Box>
          )}
          {authorities?.includes(
            generateAuthorityName(
              'CP',
              FunctionModule.ADMIN,
              NOTIFICATION_FUNCTION_MAP[featureValue],
              PermissionType.EDIT
            )
          ) && (
            <Box
              className={`${classesHistoryTranslation.classes.iconButtonContainer} ${classesHistoryTranslation.classes.editButtonContainer}`}
            >
              <EditButton
                editHandler={() =>
                  classesHistoryTranslation.history.push(
                    `/${admin}/${manageNotifications.route}/${
                      state.data[dataIndex].uuid
                    }/${selectFeatureSubPathEditTemplate(featureValue)}`,
                    {
                      featureValue,
                      defaultTab: 1,
                      page: state.page,
                      pageSize: state.size,
                    }
                  )
                }
                label={classesHistoryTranslation.t('announcementsNotifications->>Listing.Edit')}
                typographyVariant="h6"
              />
            </Box>
          )}
          {authorities?.includes(
            generateAuthorityName(
              'CP',
              FunctionModule.ADMIN,
              NOTIFICATION_FUNCTION_MAP[featureValue],
              PermissionType.DELETE
            )
          ) && (
            <Box className={classesHistoryTranslation.classes.iconButtonContainer}>
              <DeleteButton
                deleteHandler={() => {
                  setDeleteNotificationTemplateTitleUuid({
                    title: state.data[dataIndex].title,
                    uuid: state.data[dataIndex].uuid,
                  });
                  setOpenDeleteModal(true);
                }}
                label={classesHistoryTranslation.t('announcementsNotifications->>Listing.Delete')}
                typographyVariant="h6"
              />
            </Box>
          )}
        </>
      ),
    },
  },
];

interface NotificationTemplateDataTableProps {
  featureValue: string;
  featureDisplay: string;
}

const NotificationTemplateDataTable = ({
  featureValue,
  featureDisplay,
}: NotificationTemplateDataTableProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const history = useHistory<{ featureValue: string; page: number; pageSize: number }>();

  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);

  const authorities = useGetUserAuthorities();

  const isFeatureValueSMS = featureValue === 'SMS';

  const dataFromNotificationListing = history?.location?.state;

  const [deleteNotificationTemplateTitleUuid, setDeleteNotificationTemplateTitleUuid] =
    useState<AnnouncementNotificationTitleUuid>({
      title: '',
      uuid: '',
    });

  const handleDeleteModal = () => {
    mutatePostNotificationTemplateListing({
      apiPathParam: {
        page: state.page,
        pageSize: state.size,
        sort: state.sortColumn,
        order: state.order,
      },
      notificationType: featureValue,
    });
    setOpenDeleteModal(false);
  };

  const { mutateAsync: deleteNotificationTemplate } = useDeleteNotificationTemplate({});

  const handleCloseDeleteModal = () => setOpenDeleteModal(false);

  const handleDelete = async (uuid: string) => {
    await deleteNotificationTemplate({
      uuid,
      notificationType: featureValue,
    });
    handleDeleteModal();
  };

  const {
    state,
    dispatch: tableDispatch,
    tOptions,
    selectionToolbar,
  } = useDataTable({
    ...TABLE_INITIAL_STATE,
    page: dataFromNotificationListing?.page || 0,
    size: dataFromNotificationListing?.pageSize || 5,
    sortColumn: 'updatedAt',
    order: 'desc',
    search: featureValue,
  });

  const onRemoveSelectedClicked = async () => {
    const promises = Object.keys(state.rowsSelected).map((s) => {
      const targetTemplate: NotificationTemplateListingResponseContentObject =
        state.rowsSelected[s];
      return deleteNotificationTemplate({
        uuid: targetTemplate.uuid,
        notificationType: featureValue,
      });
    });

    await Promise.all(promises);

    mutatePostNotificationTemplateListing({
      apiPathParam: {
        page: state.page,
        pageSize: state.size,
        sort: state.sortColumn,
        order: state.order,
      },
      notificationType: featureValue,
    });
    tableDispatch({
      type: 'CLEAR_SELECTION',
    });
  };

  const {
    mutate: mutatePostNotificationTemplateListing,
    isLoading: isLoadingNotificationTemplateListing,
  } = usePostNotificationTemplateListing({
    onSuccess: (data) => {
      tableDispatch({
        type: 'UPDATE_DATA',
        payload: {
          data: data.content,
          count: data.totalElements,
        },
      } as TableActionTypes);
    },
  });

  useEffect(() => {
    if (featureValue) {
      tableDispatch({
        type: 'CLEAR_SELECTION',
      });
      mutatePostNotificationTemplateListing({
        apiPathParam: {
          page: state.page,
          pageSize: state.size,
          sort: state.sortColumn,
          order: state.order,
        },
        notificationType: featureValue,
      });
    }
  }, [
    mutatePostNotificationTemplateListing,
    state.order,
    state.page,
    state.size,
    state.sortColumn,
    featureValue,
    tableDispatch,
  ]);

  return (
    <>
      <Box className={classes.dataTable}>
        <MUIDataTable
          isLoading={isLoadingNotificationTemplateListing}
          state={state}
          dispatch={tableDispatch}
          columns={tableColumns(
            featureValue,
            state,
            { classes, history, t },
            setDeleteNotificationTemplateTitleUuid,
            setOpenDeleteModal,
            isFeatureValueSMS,
            authorities.data
          )}
          data={state.data}
          emptyDataLabel={t('announcementsNotifications->>Listing.EmptyDataTableMessage')}
          options={{
            ...tOptions,
            selectableRows: 'multiple',
            onRowSelectionChange: (currentRowsSelected: any, allRowsSelected: any, rowsSelected: any) => {
              if (tOptions.onRowSelectionChange) {
                // perform dataTable web-library onChange
                tOptions.onRowSelectionChange(currentRowsSelected, allRowsSelected, rowsSelected);
              }
            },
            sort: true,
            pagination: true,
            sortOrder: {
              name: state.sortColumn || 'updatedAt',
              direction: state.order || 'desc',
            },
            selectToolbarPlacement: 'replace',
          }}
          components={{
            TableToolbarSelect: () =>
              selectionToolbar([
                <Button
                  key="bulkDeleteButton"
                  variant="text"
                  color="primary"
                  onClick={onRemoveSelectedClicked}
                >
                  {t('announcementsNotifications->>Listing.DeleteSelected')}
                </Button>,
              ]),
          }}
          rowsPerPageOptions={[5, 10, 20, 40, 50]}
        />
      </Box>
      <CustomDialog
        open={openDeleteModal}
        title={t('announcementsNotifications->>Listing.DeleteTemplateTitle')}
        message={
          <HtmlTrans
            i18nKey="announcementsNotifications->>Listing.DeleteTemplate"
            values={{
              templateType: featureDisplay,
              title: deleteNotificationTemplateTitleUuid.title,
            }}
          />
        }
        action={
          <APIActions
            onClose={handleCloseDeleteModal}
            apiCall={() => handleDelete(deleteNotificationTemplateTitleUuid.uuid)}
            cancelString={t('announcementsNotifications->>Listing.Cancel')}
            apiString={t('announcementsNotifications->>Listing.Proceed')}
          />
        }
      />
    </>
  );
};

export default NotificationTemplateDataTable;
